import { useState, useEffect, useCallback, useMemo } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "../../components/ui/tabs";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription
} from "../../components/ui/sheet";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import {
  Inbox, Search, AlertTriangle, Send, Users, Info, Paperclip,
  RefreshCw, CheckCircle2, Mail, Loader2, X,
  Reply, Clock, Archive, Sparkles, Target, CalendarDays, ChevronDown
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel
} from "../../components/ui/dropdown-menu";
import DOMPurify from "dompurify";
import { API } from "../../App";
import { apiFetch } from "../../lib/apiFetch";

const CATEGORY_META = {
  urgent:        { label: "Urgent",      color: "bg-red-500/10 text-red-600 border-red-500/30",       dot: "bg-red-500" },
  to_reply:      { label: "À répondre",  color: "bg-orange-500/10 text-orange-600 border-orange-500/30", dot: "bg-orange-500" },
  to_delegate:   { label: "À déléguer",  color: "bg-purple-500/10 text-purple-600 border-purple-500/30", dot: "bg-purple-500" },
  informational: { label: "Info",        color: "bg-blue-500/10 text-blue-600 border-blue-500/30",    dot: "bg-blue-500" }
};

const InboxPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [outlookEmails, setOutlookEmails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingOutlook, setLoadingOutlook] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [outlookConnected, setOutlookConnected] = useState(false);
  const [outlookEmail, setOutlookEmail] = useState("");
  const [outlookError, setOutlookError] = useState("");
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [aiSummary, setAiSummary] = useState("");
  const [summaryError, setSummaryError] = useState("");
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [generatedReply, setGeneratedReply] = useState("");
  const [loadingDraft, setLoadingDraft] = useState(false);
  const [sendingReply, setSendingReply] = useState(false);
  const [delegateOpen, setDelegateOpen] = useState(false);
  const [colleagues, setColleagues] = useState([]);
  const [selectedColleagueId, setSelectedColleagueId] = useState("");
  const [delegateNote, setDelegateNote] = useState("");
  const [delegating, setDelegating] = useState(false);

  // Per-email status map: { [email_id]: { delegated?, followup?, archived? } }
  const [statusMap, setStatusMap] = useState({});
  // Custom follow-up date picker
  const [followupDateOpen, setFollowupDateOpen] = useState(false);
  const [followupCustomDate, setFollowupCustomDate] = useState("");
  const [followupTargetId, setFollowupTargetId] = useState(null);

  const activeCategory = searchParams.get("category") || "all";

  // OAuth callback params
  useEffect(() => {
    const success = searchParams.get("outlook_success");
    const error = searchParams.get("outlook_error");
    const email = searchParams.get("email");
    if (success === "true") {
      setOutlookConnected(true);
      setOutlookEmail(email || "");
      toast.success("Outlook connecté", { description: email || undefined });
      setSearchParams({});
    }
    if (error) {
      const map = {
        missing_code: "Code d'autorisation manquant",
        invalid_state: "Lien de connexion expiré, recommencez",
        not_configured: "Microsoft Outlook non configuré",
        token_failed: "Échec de l'authentification",
        no_token: "Token non reçu",
        callback_failed: "Erreur de callback"
      };
      toast.error(map[error] || `Erreur: ${error}`);
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);

  // Check connection on mount
  useEffect(() => {
    (async () => {
      try {
        const r = await apiFetch(`${API}/auth/microsoft/connection`);
        if (r.ok) {
          const d = await r.json();
          if (d.connected) {
            setOutlookConnected(true);
            setOutlookEmail(d.email || "");
          }
        }
      } catch (_e) { /* silent */ }
    })();
  }, []);

  const fetchOutlookEmails = useCallback(async () => {
    setLoadingOutlook(true);
    setOutlookError("");
    try {
      const r = await apiFetch(`${API}/users/me/outlook-emails`);
      const d = await r.json();
      if (!r.ok) {
        const msg = d.detail?.message_fr || d.detail?.error || "Erreur lors du chargement";
        setOutlookError(msg); setOutlookEmails([]); return;
      }
      if (d.status === "error") {
        setOutlookError(d.message_fr || "Erreur Outlook");
        setOutlookEmails([]); return;
      }
      if (d.status === "empty" || (d.emails && d.emails.length === 0)) {
        setOutlookError(d.message_fr || "Aucun email trouvé.");
        setOutlookEmails([]); return;
      }
      setOutlookEmails(d.emails || []);
      toast.success(`${d.count} email(s) chargé(s) depuis Outlook`);
    } catch (e) {
      setOutlookError(e?.message || "Erreur de connexion au serveur");
    } finally { setLoadingOutlook(false); }
  }, []);

  // Fetch per-email statuses (delegated / followup / archived)
  const fetchStatuses = useCallback(async () => {
    try {
      const r = await apiFetch(`${API}/emails-statuses`);
      if (!r.ok) return;
      const d = await r.json();
      setStatusMap(d.statuses || {});
    } catch (_) { /* silent */ }
  }, []);

  // Refresh statuses when emails list changes
  useEffect(() => {
    if (outlookEmails.length > 0) fetchStatuses();
  }, [outlookEmails, fetchStatuses]);

  // Format helpers for status badges
  const formatDayMonthYear = (iso) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch { return iso; }
  };

  const buildStatusBadges = (email, variant = "list") => {
    const s = statusMap[email?.id];
    if (!s) return [];
    const out = [];
    if (s.delegated) {
      const name = s.delegated.recipient_name || s.delegated.recipient_email || "";
      out.push({
        key: "delegated",
        label: variant === "list" ? "Délégué" : `Délégué à ${name}`,
        cls: "bg-purple-500/10 text-purple-700 dark:text-purple-300 border-purple-500/30"
      });
    }
    if (s.followup) {
      const d = s.followup.delay_days;
      const at = s.followup.remind_at;
      let label;
      if (variant === "list") {
        label = d ? `Relance J+${d}` : `Relance le ${formatDayMonthYear(at)}`;
      } else {
        label = d ? `Relance programmée — J+${d}` : `Relance programmée — le ${formatDayMonthYear(at)}`;
      }
      out.push({
        key: "followup",
        label,
        cls: "bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30"
      });
    }
    if (s.archived) {
      out.push({
        key: "archived",
        label: "Archivé",
        cls: "bg-slate-500/10 text-slate-700 dark:text-slate-300 border-slate-500/30"
      });
    }
    return out;
  };

  // Follow-up: create/update with preset days OR explicit remind_at
  const scheduleFollowup = async (email, { days, remindAt }) => {
    if (!email) return;
    try {
      const r = await apiFetch(`${API}/emails/followup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email_id: email.id,
          days: days ?? undefined,
          remind_at: remindAt ?? undefined,
          subject: email.subject || "",
          sender_email: email.sender_email || "",
          source: email.source || "outlook"
        })
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        toast.error(d.detail || "Impossible de programmer la relance");
        return;
      }
      const label = days ? `J+${days}` : `le ${formatDayMonthYear(d.remind_at)}`;
      toast.success(`Relance programmée ${label}`);
      fetchStatuses();
    } catch (e) {
      toast.error(e.message || "Erreur réseau lors de la programmation");
    }
  };

  const cancelFollowup = async (email) => {
    if (!email) return;
    try {
      const r = await apiFetch(`${API}/emails/followup/${encodeURIComponent(email.id)}`, {
        method: "DELETE"
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        toast.error(d.detail || "Impossible d'annuler la relance");
        return;
      }
      toast.success("Relance annulée");
      fetchStatuses();
    } catch (e) {
      toast.error(e.message || "Erreur réseau lors de l'annulation");
    }
  };

  const openCustomFollowupPicker = (email) => {
    setFollowupTargetId(email.id);
    // Default = tomorrow, formatted for <input type=date/>
    const d = new Date();
    d.setDate(d.getDate() + 1);
    setFollowupCustomDate(d.toISOString().slice(0, 10));
    setFollowupDateOpen(true);
  };

  const submitCustomFollowup = async () => {
    if (!followupCustomDate) return;
    const target = outlookEmails.find(e => e.id === followupTargetId) || selectedEmail;
    if (!target) { setFollowupDateOpen(false); return; }
    // Combine chosen date at 09:00 local time
    const dt = new Date(`${followupCustomDate}T09:00:00`);
    await scheduleFollowup(target, { remindAt: dt.toISOString() });
    setFollowupDateOpen(false);
  };

  // Auto-fetch emails on first connect detection
  useEffect(() => {
    if (outlookConnected && outlookEmails.length === 0 && !outlookError) {
      fetchOutlookEmails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outlookConnected]);

  const openEmail = async (email) => {
    setSelectedEmail({ ...email, _loading: true });
    setLoadingDetail(true);
    setAiSummary("");
    setSummaryError("");
    setGeneratedReply("");
    try {
      const r = await apiFetch(`${API}/users/me/outlook-emails/${encodeURIComponent(email.id)}`);
      if (!r.ok) {
        toast.error("Impossible d'ouvrir l'email");
        setSelectedEmail(null);
        return;
      }
      const d = await r.json();
      const merged = { ...email, ...d, _loading: false };
      setSelectedEmail(merged);
      // Auto-trigger AI summary in the background
      requestSummary(merged);
    } catch (_e) {
      toast.error("Erreur d'ouverture de l'email");
      setSelectedEmail(null);
    } finally { setLoadingDetail(false); }
  };

  const stripHtml = (s) => (s || "").replace(/<style[\s\S]*?<\/style>/gi, " ").replace(/<script[\s\S]*?<\/script>/gi, " ").replace(/<[^>]*>/g, " ").replace(/&nbsp;/g, " ").replace(/\s+/g, " ").trim();

  const getPlainBody = (email) => {
    const raw = email.body_content || "";
    const isHtml = email.body_content_type === "html" || /<\/?(html|body|div|p|span|table)/i.test(raw);
    const text = isHtml ? stripHtml(raw) : raw;
    return (text || email.body_preview || "").slice(0, 8000);
  };

  const requestSummary = async (email) => {
    const bodyText = getPlainBody(email);
    if (!bodyText && !email.subject) {
      setSummaryError("Aucun contenu à résumer");
      return;
    }
    setLoadingSummary(true);
    setSummaryError("");
    try {
      const r = await apiFetch(`${API}/ai/summarize-content`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          subject: email.subject || "",
          from_name: email.sender_name || "",
          from_email: email.sender_email || "",
          body: bodyText
        })
      });
      if (!r.ok) {
        let msg = `Erreur ${r.status}`;
        try { const d = await r.json(); msg = d.detail || msg; } catch (_) { /* ignore */ }
        setSummaryError(msg);
        return;
      }
      const d = await r.json();
      setAiSummary(d.summary || "");
      if (!d.summary) setSummaryError("Résumé vide retourné par l'IA");
    } catch (e) {
      setSummaryError(e.message || "Erreur réseau lors du résumé");
    } finally {
      setLoadingSummary(false);
    }
  };

  const handleGenerateReply = async () => {
    if (!selectedEmail) return;
    const bodyText = getPlainBody(selectedEmail);
    setLoadingDraft(true);
    try {
      const r = await apiFetch(`${API}/ai/draft-content`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          subject: selectedEmail.subject || "",
          from_name: selectedEmail.sender_name || "",
          from_email: selectedEmail.sender_email || "",
          body: bodyText,
          tone: "formal"
        })
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        toast.error(d.detail || `Erreur ${r.status} lors de la génération`);
        return;
      }
      setGeneratedReply(d.draft || "");
      if (!d.draft) toast.error("Réponse vide retournée par l'IA");
    } catch (e) {
      toast.error(e.message || "Erreur réseau lors de la génération");
    } finally {
      setLoadingDraft(false);
    }
  };

  const openDelegate = async () => {
    setSelectedColleagueId("");
    setDelegateNote("");
    setDelegateOpen(true);
    // Load colleagues on demand
    try {
      const r = await apiFetch(`${API}/users/me/colleagues`);
      if (r.ok) {
        const d = await r.json();
        setColleagues(d.colleagues || []);
      }
    } catch (_) { /* silent */ }
  };

  const handleDelegateSubmit = async (e) => {
    e?.preventDefault?.();
    if (!selectedEmail) return;
    if (!selectedColleagueId) {
      toast.error("Sélectionnez un collègue dans la liste");
      return;
    }
    setDelegating(true);
    try {
      const r = await apiFetch(`${API}/users/me/outlook-emails/${encodeURIComponent(selectedEmail.id)}/delegate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ colleague_id: selectedColleagueId, note: delegateNote })
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        toast.error(d.detail || `Erreur ${r.status} lors de la délégation`);
        return;
      }
      toast.success(d.message || "E-mail délégué avec succès");
      setDelegateOpen(false);
      fetchStatuses();
    } catch (err) {
      toast.error(err.message || "Erreur réseau lors de la délégation");
    } finally {
      setDelegating(false);
    }
  };


  const handleSendReply = async () => {
    if (!selectedEmail || !generatedReply.trim()) return;
    setSendingReply(true);
    try {
      const r = await apiFetch(`${API}/users/me/outlook-emails/${encodeURIComponent(selectedEmail.id)}/reply`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ body: generatedReply, content_type: "Text" })
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        toast.error(d.detail || `Erreur ${r.status} lors de l'envoi`);
        return;
      }
      toast.success("Réponse envoyée via Outlook");
      setSelectedEmail(null);
      setGeneratedReply("");
    } catch (e) {
      toast.error(e.message || "Erreur réseau lors de l'envoi");
    } finally {
      setSendingReply(false);
    }
  };

  const recordAction = async (email, actionType) => {
    const labels = {
      reply_now: "Brouillon de réponse créé",
      mark_priority: "Email marqué prioritaire",
      reply: "Brouillon de réponse créé",
      followup_3d: "Relance planifiée dans 3 jours",
      forward: "Préparé pour transfert",
      delegate: "Email marqué à déléguer",
      archive: "Email archivé",
      unsubscribe: "Désinscription demandée"
    };
    try {
      await apiFetch(`${API}/emails/action`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email_id: email.id,
          action_type: actionType,
          source: email.source || "outlook",
          subject: email.subject || "",
          sender_email: email.sender_email || ""
        })
      });
      toast.success(labels[actionType] || "Action enregistrée");
      if (actionType === "archive") fetchStatuses();
    } catch (_e) {
      toast.error("L'action n'a pas pu être enregistrée");
    }
  };

  // Stats from loaded emails — auto-update from real data
  const stats = useMemo(() => {
    const s = { all: outlookEmails.length, urgent: 0, to_reply: 0, to_delegate: 0, informational: 0, unread: 0 };
    for (const e of outlookEmails) {
      if (e.category && s[e.category] !== undefined) s[e.category]++;
      if (!e.is_read) s.unread++;
    }
    return s;
  }, [outlookEmails]);

  // Real number of delegations (from Microsoft Graph delegate action) — from statusMap
  const delegatedCount = useMemo(
    () => Object.values(statusMap || {}).filter((s) => s && s.delegated).length,
    [statusMap]
  );

  const categories = [
    { id: "all", label: "Tous", count: stats.all },
    { id: "urgent", label: "Urgent", count: stats.urgent, icon: AlertTriangle, color: "text-red-500" },
    { id: "to_reply", label: "À répondre", count: stats.to_reply, icon: Send, color: "text-orange-500" },
    { id: "to_delegate", label: "À déléguer", count: stats.to_delegate, icon: Users, color: "text-purple-500" },
    { id: "informational", label: "Info", count: stats.informational, icon: Info, color: "text-blue-500" }
  ];

  const filteredEmails = useMemo(() => {
    let list = outlookEmails;
    if (activeCategory !== "all") list = list.filter(e => e.category === activeCategory);
    const q = searchQuery.toLowerCase().trim();
    if (q) {
      list = list.filter(e =>
        (e.subject || "").toLowerCase().includes(q) ||
        (e.sender_name || "").toLowerCase().includes(q) ||
        (e.sender_email || "").toLowerCase().includes(q)
      );
    }
    return list;
  }, [outlookEmails, activeCategory, searchQuery]);

  const formatDate = (s) => {
    if (!s) return "";
    const date = new Date(s);
    const now = new Date();
    const diff = Math.floor((now - date) / 3600000);
    if (diff < 1) return "À l'instant";
    if (diff < 24) return `Il y a ${diff}h`;
    if (diff < 48) return "Hier";
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };

  if (loading) {
    return (
      <div className="p-3 sm:p-6 lg:p-8">
        <div className="animate-pulse space-y-3 sm:space-y-4">
          <div className="h-8 w-48 bg-slate-200 dark:bg-slate-800 rounded"></div>
          <div className="h-11 bg-slate-200 dark:bg-slate-800 rounded"></div>
          {[1,2,3,4,5].map(i => <div key={i} className="h-20 sm:h-24 bg-slate-200 dark:bg-slate-800 rounded-xl" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-6 lg:p-8 space-y-4 sm:space-y-6" data-testid="inbox-page">
      {/* Outlook Connection Banner */}
      {outlookConnected && (
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 p-3 sm:p-4 rounded-xl bg-green-500/10 border border-green-500/20" data-testid="outlook-connected-banner">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-green-700 dark:text-green-400">Outlook connecté</p>
              {outlookEmail && <p className="text-xs text-green-600 dark:text-green-500 truncate">{outlookEmail}</p>}
            </div>
          </div>
          <Button
            size="sm"
            onClick={fetchOutlookEmails}
            disabled={loadingOutlook}
            className="bg-green-600 hover:bg-green-700 text-white min-h-[44px] w-full sm:w-auto"
            data-testid="load-outlook-emails-btn"
          >
            {loadingOutlook ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Chargement...</>
            ) : (
              <><RefreshCw className="w-4 h-4 mr-2" />Recharger</>
            )}
          </Button>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-slate-900 dark:text-white" style={{ fontFamily: 'Outfit' }}>
            Boîte de réception
          </h1>
          <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mt-1" data-testid="inbox-stats">
            {outlookEmails.length > 0
              ? `${outlookEmails.length} email(s) Outlook · ${stats.unread} non lu(s)`
              : outlookError
                ? "Impossible de charger les emails"
                : outlookConnected ? "Aucun email pour le moment" : "Connectez Outlook pour voir vos emails"}
          </p>
        </div>
        <div className="relative w-full sm:w-auto">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Rechercher..."
            className="pl-10 w-full sm:w-64 h-11"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            data-testid="search-input"
          />
        </div>
      </div>

      {outlookError && outlookEmails.length === 0 && (
        <div className="flex items-center gap-3 p-3 sm:p-4 rounded-xl bg-amber-500/10 border border-amber-500/20" data-testid="outlook-error-banner">
          <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0" />
          <p className="text-sm text-amber-700 dark:text-amber-400">{outlookError}</p>
        </div>
      )}

      {/* Stat cards — Non lus / Urgent / À répondre / Délégués */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3" data-testid="inbox-stat-cards">
        {[
          { key: "unread",       label: "Non lus",     value: stats.unread,       color: "bg-blue-500/10 text-blue-600 dark:text-blue-400" },
          { key: "urgent",       label: "Urgent",      value: stats.urgent,       color: "bg-red-500/10 text-red-600 dark:text-red-400" },
          { key: "to_reply",     label: "À répondre",  value: stats.to_reply,     color: "bg-orange-500/10 text-orange-600 dark:text-orange-400" },
          { key: "to_delegate",  label: "Délégués",    value: delegatedCount,  color: "bg-purple-500/10 text-purple-600 dark:text-purple-400" }
        ].map((s) => (
          <div key={s.key} className={`rounded-xl p-3 sm:p-4 text-center ${s.color}`} data-testid={`stat-card-${s.key}`}>
            <p className="text-2xl sm:text-3xl font-bold">{s.value}</p>
            <p className="text-xs sm:text-sm opacity-80 mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Category Tabs (counters auto-update) */}
      <Tabs value={activeCategory} onValueChange={(v) => setSearchParams(v === "all" ? {} : { category: v })}>
        <TabsList className="bg-slate-100 dark:bg-slate-800 h-auto flex-wrap w-full sm:w-auto gap-1 p-1">
          {categories.map((cat) => (
            <TabsTrigger
              key={cat.id}
              value={cat.id}
              className="flex items-center gap-1.5 sm:gap-2 min-h-[40px] px-2 sm:px-3 data-[state=active]:bg-white dark:data-[state=active]:bg-slate-700"
              data-testid={`tab-${cat.id}`}
            >
              {cat.icon && <cat.icon className={`w-3.5 h-3.5 sm:w-4 sm:h-4 ${cat.color || ''}`} />}
              <span className="text-xs sm:text-sm">{cat.label}</span>
              <Badge variant="secondary" className="text-[10px] sm:text-xs px-1.5" data-testid={`tab-count-${cat.id}`}>
                {cat.count}
              </Badge>
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {/* Email List */}
      <div className="space-y-2">
        {filteredEmails.map((email) => {
          const meta = CATEGORY_META[email.category] || CATEGORY_META.informational;
          return (
            <Card
              key={email.id}
              role="button"
              tabIndex={0}
              onClick={() => openEmail(email)}
              onKeyDown={(e) => { if (e.key === "Enter") openEmail(email); }}
              className={`glass-card hover:shadow-md transition-all cursor-pointer ${!email.is_read ? 'border-l-4 border-l-blue-500' : ''}`}
              data-testid={`outlook-email-${email.id.slice(-20)}`}
            >
              <CardContent className="p-3 sm:p-4">
                <div className="flex items-start gap-2 sm:gap-3">
                  <span className={`mt-2 w-2.5 h-2.5 rounded-full ${meta.dot} flex-shrink-0`} title={meta.label} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <span className={`text-sm font-medium truncate ${!email.is_read ? 'text-slate-900 dark:text-white' : 'text-slate-600 dark:text-slate-400'}`}>
                        {email.sender_name || email.sender_email}
                      </span>
                      <span className="text-[11px] sm:text-xs text-slate-500 flex-shrink-0">{formatDate(email.received_at)}</span>
                    </div>
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className={`text-sm truncate ${!email.is_read ? 'font-semibold text-slate-900 dark:text-white' : 'text-slate-700 dark:text-slate-300'}`}>
                        {email.subject}
                      </h3>
                      {email.has_attachments && <Paperclip className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-slate-400 flex-shrink-0" />}
                    </div>
                    <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 truncate">{email.body_preview}</p>
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      <Badge variant="outline" className={`${meta.color} text-[10px] sm:text-xs`} data-testid={`category-badge-${email.category}`}>
                        {meta.label}
                      </Badge>
                      {email.importance === "high" && (
                        <Badge variant="outline" className="bg-red-500/10 text-red-500 border-red-500/20 text-[10px] sm:text-xs">
                          Important
                        </Badge>
                      )}
                      {buildStatusBadges(email, "list").map((b) => (
                        <Badge
                          key={b.key}
                          variant="outline"
                          className={`${b.cls} text-[10px] sm:text-xs`}
                          data-testid={`status-badge-list-${b.key}-${email.id.slice(-8)}`}
                        >
                          {b.label}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}

        {/* Empty state */}
        {filteredEmails.length === 0 && !outlookError && (
          <Card className="glass-card" data-testid="empty-inbox">
            <CardContent className="p-8 sm:p-12 text-center">
              <Inbox className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">Aucun email</h3>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                {searchQuery
                  ? "Aucun email ne correspond à votre recherche."
                  : outlookConnected
                    ? "Cliquez sur \"Recharger\" pour récupérer vos emails Outlook."
                    : "Connectez votre compte Outlook pour voir vos emails."}
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Email Detail Drawer */}
      <Sheet open={!!selectedEmail} onOpenChange={(o) => { if (!o) setSelectedEmail(null); }}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-2xl overflow-y-auto p-0"
          data-testid="email-detail-drawer"
        >
          {selectedEmail && (
            <div className="flex flex-col h-full">
              <SheetHeader className="p-4 sm:p-6 border-b border-slate-200 dark:border-slate-700 sticky top-0 bg-white dark:bg-slate-900 z-10">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge
                      variant="outline"
                      className={`${(CATEGORY_META[selectedEmail.category] || CATEGORY_META.informational).color} text-xs`}
                    >
                      {(CATEGORY_META[selectedEmail.category] || CATEGORY_META.informational).label}
                    </Badge>
                    {buildStatusBadges(selectedEmail, "detail").map((b) => (
                      <Badge
                        key={b.key}
                        variant="outline"
                        className={`${b.cls} text-xs`}
                        data-testid={`status-badge-detail-${b.key}`}
                      >
                        {b.label}
                      </Badge>
                    ))}
                  </div>
                  <button
                    onClick={() => setSelectedEmail(null)}
                    className="p-1.5 rounded-md hover:bg-slate-100 dark:hover:bg-slate-800"
                    aria-label="Close"
                    data-testid="email-detail-close"
                  >
                    <X className="w-5 h-5 text-slate-500" />
                  </button>
                </div>
                <SheetTitle className="text-left text-base sm:text-lg leading-snug pr-6 break-words" data-testid="email-detail-subject">
                  {selectedEmail.subject}
                </SheetTitle>
                <SheetDescription asChild>
                  <div className="space-y-1 text-left">
                    <div className="text-sm text-slate-700 dark:text-slate-300" data-testid="email-detail-sender">
                      <span className="font-medium">{selectedEmail.sender_name}</span>
                      <span className="text-slate-500"> &lt;{selectedEmail.sender_email}&gt;</span>
                    </div>
                    <div className="text-xs text-slate-500 dark:text-slate-400" data-testid="email-detail-date">
                      {selectedEmail.received_at
                        ? new Date(selectedEmail.received_at).toLocaleString('fr-FR', {
                            day: 'numeric', month: 'long', year: 'numeric',
                            hour: '2-digit', minute: '2-digit'
                          })
                        : ""}
                    </div>
                    {selectedEmail.to_recipients?.length > 0 && (
                      <div className="text-xs text-slate-500 dark:text-slate-400">
                        À : {selectedEmail.to_recipients.join(", ")}
                      </div>
                    )}
                  </div>
                </SheetDescription>
              </SheetHeader>

              <div className="flex-1 p-4 sm:p-6 space-y-6">
                {/* AI Summary block */}
                <div className="p-4 rounded-xl bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800" data-testid="ai-summary-block">
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles className="w-4 h-4 text-blue-600" />
                    <span className="font-medium text-blue-900 dark:text-blue-300">Résumé IA</span>
                  </div>
                  {loadingSummary ? (
                    <div className="flex items-center gap-2 text-sm text-blue-800 dark:text-blue-200">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Génération du résumé…
                    </div>
                  ) : aiSummary ? (
                    <p className="text-sm text-blue-800 dark:text-blue-200 whitespace-pre-wrap" data-testid="ai-summary-text">
                      {aiSummary}
                    </p>
                  ) : summaryError ? (
                    <div className="flex items-start justify-between gap-3">
                      <p className="text-sm text-red-700 dark:text-red-300 flex-1" data-testid="ai-summary-error">
                        {summaryError}
                      </p>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => requestSummary(selectedEmail)}
                        data-testid="retry-summary-btn"
                      >
                        <RefreshCw className="w-3.5 h-3.5 mr-1" />
                        Réessayer
                      </Button>
                    </div>
                  ) : (
                    <p className="text-sm text-blue-700/70 dark:text-blue-300/70 italic">
                      Résumé indisponible pour le moment.
                    </p>
                  )}
                </div>

                {/* Suggested Actions — always 4 unified buttons */}
                <div>
                  <h4 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3 flex items-center gap-2">
                    <Target className="w-4 h-4 text-emerald-500" />
                    Actions suggérées
                  </h4>
                  <div className="flex flex-wrap gap-2" data-testid="email-action-bar">
                    <Button
                      size="sm"
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                      onClick={handleGenerateReply}
                      disabled={loadingDraft}
                      data-testid="action-reply"
                    >
                      <Reply className="w-3.5 h-3.5 mr-1" />
                      {loadingDraft ? "Génération…" : "Répondre"}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={openDelegate}
                      data-testid="action-delegate"
                    >
                      <Users className="w-3.5 h-3.5 mr-1" />
                      Déléguer
                    </Button>
                    {/* Follow-up dropdown menu — configurable delay */}
                    {(() => {
                      const existing = statusMap[selectedEmail.id]?.followup;
                      return (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              size="sm"
                              variant="outline"
                              data-testid="action-followup"
                            >
                              <Clock className="w-3.5 h-3.5 mr-1" />
                              {existing
                                ? (existing.delay_days
                                    ? `Relance J+${existing.delay_days}`
                                    : `Relance le ${formatDayMonthYear(existing.remind_at)}`)
                                : "Relancer"}
                              <ChevronDown className="w-3.5 h-3.5 ml-1" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="start" className="w-56" data-testid="followup-menu">
                            <DropdownMenuLabel>
                              {existing ? "Modifier la relance" : "Programmer une relance"}
                            </DropdownMenuLabel>
                            {[1, 2, 3, 5, 7].map((d) => (
                              <DropdownMenuItem
                                key={d}
                                onSelect={() => scheduleFollowup(selectedEmail, { days: d })}
                                data-testid={`followup-preset-${d}`}
                              >
                                <Clock className="w-3.5 h-3.5 mr-2 text-amber-500" />
                                J+{d}
                              </DropdownMenuItem>
                            ))}
                            <DropdownMenuItem
                              onSelect={() => openCustomFollowupPicker(selectedEmail)}
                              data-testid="followup-custom-date"
                            >
                              <CalendarDays className="w-3.5 h-3.5 mr-2 text-amber-500" />
                              Choisir une date…
                            </DropdownMenuItem>
                            {existing && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  onSelect={() => cancelFollowup(selectedEmail)}
                                  data-testid="followup-cancel"
                                >
                                  <X className="w-3.5 h-3.5 mr-2 text-red-500" />
                                  Annuler la relance
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      );
                    })()}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => recordAction(selectedEmail, "archive")}
                      data-testid="action-archive"
                    >
                      <Archive className="w-3.5 h-3.5 mr-1" />
                      Archiver
                    </Button>
                  </div>
                </div>

                {/* Email body */}
                {loadingDetail || selectedEmail._loading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                  </div>
                ) : selectedEmail.body_content_type === "html" ? (
                  <div
                    className="prose prose-sm dark:prose-invert max-w-none email-html-body p-4 rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700"
                    data-testid="email-detail-body"
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(selectedEmail.body_content || "", {
                        FORBID_TAGS: ["script", "style", "iframe", "object", "embed", "form"],
                        FORBID_ATTR: ["onerror", "onload", "onclick", "onmouseover"]
                      })
                    }}
                  />
                ) : (
                  <pre
                    className="whitespace-pre-wrap font-sans text-sm text-slate-700 dark:text-slate-200 break-words p-4 rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700"
                    data-testid="email-detail-body"
                  >
                    {selectedEmail.body_content || selectedEmail.body_preview || "(Aucun contenu)"}
                  </pre>
                )}

                {/* Generate Reply */}
                <div className="border-t border-slate-200 dark:border-slate-700 pt-6">
                  {!generatedReply ? (
                    <Button
                      onClick={handleGenerateReply}
                      disabled={loadingDraft}
                      className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-5"
                      data-testid="generate-reply-btn"
                    >
                      {loadingDraft ? (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                          Génération en cours…
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4 mr-2" />
                          Générer une réponse
                        </>
                      )}
                    </Button>
                  ) : (
                    <div data-testid="generated-reply-block">
                      <div className="flex items-center gap-2 mb-3">
                        <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                        <span className="font-medium text-emerald-700 dark:text-emerald-400">
                          Réponse générée
                        </span>
                      </div>
                      <textarea
                        value={generatedReply}
                        onChange={(e) => setGeneratedReply(e.target.value)}
                        rows={10}
                        className="w-full p-4 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 text-sm text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-y"
                        data-testid="generated-reply-textarea"
                      />
                      <div className="flex flex-wrap gap-2 mt-4">
                        <Button
                          onClick={handleSendReply}
                          disabled={sendingReply || !generatedReply.trim()}
                          className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                          data-testid="send-reply-btn"
                        >
                          {sendingReply ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Envoi…
                            </>
                          ) : (
                            <>
                              <Send className="w-4 h-4 mr-2" />
                              Envoyer
                            </>
                          )}
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => setGeneratedReply("")}
                          disabled={sendingReply}
                          data-testid="regenerate-reply-btn"
                        >
                          <RefreshCw className="w-4 h-4 mr-2" />
                          Regénérer
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Delegate Modal */}
      <Dialog open={delegateOpen} onOpenChange={(o) => !delegating && setDelegateOpen(o)}>
        <DialogContent className="sm:max-w-lg" data-testid="delegate-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-purple-600" />
              Déléguer cet e-mail
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleDelegateSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5" htmlFor="delegate-colleague">
                Collègue
              </label>
              {colleagues.length === 0 ? (
                <div className="rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/60 p-3 text-sm text-slate-600 dark:text-slate-400" data-testid="delegate-no-colleagues">
                  Aucun collègue enregistré. Ajoutez-en depuis <Link to="/dashboard/settings" className="text-purple-600 hover:underline font-medium" onClick={() => setDelegateOpen(false)}>Paramètres &gt; Mes collègues</Link>.
                </div>
              ) : (
                <select
                  id="delegate-colleague"
                  required
                  value={selectedColleagueId}
                  onChange={(e) => setSelectedColleagueId(e.target.value)}
                  data-testid="delegate-colleague-select"
                  className="w-full h-11 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 text-sm text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <option value="">-- Sélectionnez un collègue --</option>
                  {colleagues.map((c) => (
                    <option key={c.colleague_id} value={c.colleague_id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5" htmlFor="delegate-note">
                Note optionnelle
              </label>
              <textarea
                id="delegate-note"
                rows={3}
                placeholder="Pourquoi vous déléguez cet e-mail…"
                value={delegateNote}
                onChange={(e) => setDelegateNote(e.target.value)}
                data-testid="delegate-note-input"
                className="w-full rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-3 text-sm text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-purple-500 resize-y"
              />
            </div>

            <DialogFooter className="gap-2 sm:gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setDelegateOpen(false)}
                disabled={delegating}
                data-testid="delegate-cancel-btn"
              >
                Annuler
              </Button>
              <Button
                type="submit"
                disabled={delegating || !selectedColleagueId}
                className="bg-purple-600 hover:bg-purple-700 text-white"
                data-testid="delegate-submit-btn"
              >
                {delegating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Envoi en cours…
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Déléguer
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Follow-up custom date picker */}
      <Dialog open={followupDateOpen} onOpenChange={setFollowupDateOpen}>
        <DialogContent className="sm:max-w-sm" data-testid="followup-date-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarDays className="w-5 h-5 text-amber-500" />
              Choisir une date de relance
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <label htmlFor="followup-date-input" className="block text-sm font-medium text-slate-700 dark:text-slate-300">
              Date
            </label>
            <input
              id="followup-date-input"
              type="date"
              min={new Date(Date.now() + 24 * 3600 * 1000).toISOString().slice(0, 10)}
              value={followupCustomDate}
              onChange={(e) => setFollowupCustomDate(e.target.value)}
              data-testid="followup-date-input"
              className="w-full h-11 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 text-sm text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-amber-500"
            />
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setFollowupDateOpen(false)}
              data-testid="followup-date-cancel"
            >
              Annuler
            </Button>
            <Button
              type="button"
              className="bg-amber-500 hover:bg-amber-600 text-white"
              onClick={submitCustomFollowup}
              disabled={!followupCustomDate}
              data-testid="followup-date-confirm"
            >
              <Clock className="w-4 h-4 mr-2" />
              Programmer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InboxPage;
